import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Edit, Building, Phone, Mail, MapPin, Users, Star, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ContactGrid, ContactModal, DeleteContactModal } from '../components/contacts';
import { useContacts } from '../hooks/useContacts';
import { apiService } from '../services/api';
import { formatPhoneNumber } from '../utils/formatters';

const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clientId = parseInt(id!);

  // Fetch client details
  const {
    data: clientResponse,
    isLoading: clientLoading,
    error: clientError
  } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => apiService.getClient(clientId),
    enabled: !!clientId
  });

  // Fetch client locations
  const {
    data: locationsResponse,
    isLoading: locationsLoading,
    error: locationsError
  } = useQuery({
    queryKey: ['client-locations', clientId],
    queryFn: () => apiService.getClientLocations(clientId),
    enabled: !!clientId
  });

  // Use contacts hook
  const {
    contacts,
    primaryContact,
    isLoadingContacts,
    isModalOpen,
    isDeleteModalOpen,
    contactToDelete,
    handleAddNew,
    handleEdit,
    handleDelete,
    handleSetPrimary,
    handleSubmit,
    handleConfirmDelete,
    setIsModalOpen,
    setIsDeleteModalOpen
  } = useContacts({ contactType: 'client', parentId: clientId });

  if (clientLoading || locationsLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (clientError || !clientResponse?.data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/clients')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Clients</span>
          </Button>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center text-red-600">
            Error loading client details. Please try again.
          </div>
        </div>
      </div>
    );
  }

  const client = clientResponse.data;
  const locations = locationsResponse?.data || [];
  
  // Get primary location or first available location
  const primaryLocation = locations.find(loc => loc.isPrimary) || locations[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/clients')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Clients</span>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{client.name}</h1>
            <p className="text-sm text-gray-500">Client Details</p>
          </div>
        </div>
        <Button
          onClick={() => navigate(`/clients/${client.id}/edit`)}
          className="flex items-center space-x-2"
        >
          <Edit className="h-4 w-4" />
          <span>Edit Client</span>
        </Button>
      </div>

      {/* Main Content - Single Column Layout */}
      <div className="space-y-6">
        {/* Client Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Client Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center space-x-2 text-sm">
                <Building className="h-4 w-4" />
                <span>Client Info</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Status</span>
                <Badge variant={client.isActive ? 'success' : 'secondary'} className="text-xs">
                  {client.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="text-sm font-medium text-gray-900">{client.name}</div>
              {client.phone && (
                <div className="text-xs text-gray-600">{formatPhoneNumber(client.phone)}</div>
              )}
              {client.email && (
                <div className="text-xs text-gray-600">{client.email}</div>
              )}
              {client.rank && (
                <div className="text-xs text-gray-600">Rank: {client.rank}</div>
              )}
            </CardContent>
          </Card>

          {/* Primary Location Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center space-x-2 text-sm">
                <MapPin className="h-4 w-4" />
                <span>Primary Location</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {primaryLocation ? (
                <>
                  <div className="text-sm font-medium text-gray-900">{primaryLocation.locationName}</div>
                  {primaryLocation.address && (
                    <div className="text-xs text-gray-600">{primaryLocation.address}</div>
                  )}
                  {(primaryLocation.city || primaryLocation.state) && (
                    <div className="text-xs text-gray-600">
                      {[primaryLocation.city, primaryLocation.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    {primaryLocation.locationType} • {primaryLocation.isActive ? 'Active' : 'Inactive'}
                  </div>
                </>
              ) : (
                <div className="text-xs text-gray-500">No primary location</div>
              )}
            </CardContent>
          </Card>

          {/* Primary Contact Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center space-x-2 text-sm">
                <Users className="h-4 w-4" />
                <span>Primary Contact</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {primaryContact ? (
                <>
                  <div className="text-sm font-medium text-gray-900">{primaryContact.fullName}</div>
                  {primaryContact.title && (
                    <div className="text-xs text-gray-600">{primaryContact.title}</div>
                  )}
                  {primaryContact.email && (
                    <div className="text-xs text-gray-600">{primaryContact.email}</div>
                  )}
                  {primaryContact.phone && (
                    <div className="text-xs text-gray-600">{formatPhoneNumber(primaryContact.phone)}</div>
                  )}
                </>
              ) : (
                <div className="text-xs text-gray-500">No primary contact</div>
              )}
            </CardContent>
          </Card>

          {/* Summary Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center space-x-2 text-sm">
                <Building className="h-4 w-4" />
                <span>Summary</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="text-xs text-gray-600">
                <span className="font-medium">{locations?.length || 0}</span> locations
              </div>
              <div className="text-xs text-gray-600">
                <span className="font-medium">{contacts?.length || 0}</span> contacts
              </div>
              <div className="text-xs text-gray-600">
                Created: {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'N/A'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Locations Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5" />
                <span>Locations ({locations?.length || 0})</span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {locationsLoading ? (
              <div className="text-sm text-gray-500">Loading locations...</div>
            ) : locations && locations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {locations.map((location) => (
                  <div key={location.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium text-gray-900">{location.locationName}</span>
                          {location.isPrimary && (
                            <Badge variant="secondary" className="flex items-center space-x-1 text-xs">
                              <Star className="h-3 w-3" />
                              <span>Primary</span>
                            </Badge>
                          )}
                        </div>
                        <Badge variant={location.isActive ? 'success' : 'secondary'} className="text-xs mb-2">
                          {location.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div><span className="font-medium">Type:</span> {location.locationType}</div>
                      {location.address && (
                        <div><span className="font-medium">Address:</span> {location.address}</div>
                      )}
                      {(location.city || location.state || location.zipCode) && (
                        <div>{[location.city, location.state, location.zipCode].filter(Boolean).join(', ')}</div>
                      )}
                      {location.contactName && (
                        <div><span className="font-medium">Contact:</span> {location.contactName}</div>
                      )}
                      {location.contactPhone && (
                        <div><span className="font-medium">Phone:</span> {formatPhoneNumber(location.contactPhone)}</div>
                      )}
                      {location.contactEmail && (
                        <div><span className="font-medium">Email:</span> {location.contactEmail}</div>
                      )}
                      <div>
                        <span className="font-medium">Tax Rate:</span>{' '}
                        {location.taxRate !== null && location.taxRate !== undefined ? (
                          <span className="text-green-600">{(Number(location.taxRate) * 100).toFixed(2)}%</span>
                        ) : (
                          <span className="text-gray-500">Using company default</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">No locations found</div>
            )}
          </CardContent>
        </Card>

        {/* Contacts Section - Compact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>Contacts ({contacts?.length || 0})</span>
              </div>
              <Button
                onClick={handleAddNew}
                size="sm"
                className="flex items-center space-x-2"
              >
                <Users className="h-4 w-4" />
                <span>Add Contact</span>
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ContactGrid
              contacts={contacts}
              isLoading={isLoadingContacts}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onSetPrimary={handleSetPrimary}
              onAddNew={handleAddNew}
              contactType="client"
            />
          </CardContent>
        </Card>
      </div>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contactType="client"
        parentId={clientId}
        onSubmit={handleSubmit}
        isLoading={false}
      />

      {/* Delete Contact Modal */}
      <DeleteContactModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        contact={contactToDelete}
        contactType="client"
        onConfirm={handleConfirmDelete}
        isLoading={false}
      />
    </div>
  );
};

export default ClientDetailPage;
